// @ts-nocheck
/**
 * ツインキーシステム - ダンジョン探索画面
 * @module twinKey/ui/screens/DungeonScreen
 */

import React, { useEffect, useMemo } from 'react';
import { useTwinKeyContext } from '../../context/TwinKeyContext';
import { useDungeon, useTwinKey } from '../../hooks';
import { getEquipmentModifiedStats } from '../../core';
import styles from '../styles/twinKeyStyles';
import {
  ATTRIBUTE_MATERIALS,
  CATEGORY_MATERIALS,
  GENERIC_MATERIALS,
  RARITY_FRAGMENTS,
  EXP_ORB_MATERIALS,
} from '../../content/alchemy/data/materialTypes';

/**
 * 素材IDまたは日本語名から素材情報を取得
 */
function getMaterialByIdOrName(materialIdOrName) {
  const allMaterials = {
    ...ATTRIBUTE_MATERIALS,
    ...CATEGORY_MATERIALS,
    ...GENERIC_MATERIALS,
    ...RARITY_FRAGMENTS,
    ...EXP_ORB_MATERIALS,
  };

  // 英語IDで検索
  if (allMaterials[materialIdOrName]) {
    return allMaterials[materialIdOrName];
  }

  // 日本語名で検索
  for (const mat of Object.values(allMaterials)) {
    if (mat.name === materialIdOrName) {
      return mat;
    }
  }

  // 見つからない場合はデフォルト（日本語名をそのまま表示）
  return { id: materialIdOrName, name: materialIdOrName, icon: '📦', tier: 'common' };
}

// 難易度文字列から星の数を取得
const DIFFICULTY_STARS = {
  '初級': 1,
  '中級': 2,
  '上級': 3,
  '超級': 5,
};

/**
 * ダンジョン探索画面
 */
export function DungeonScreen() {
  const { state } = useTwinKeyContext();
  const {
    dungeon,
    currentFloor,
    maxFloor,
    floorChoices,
    selectFloorChoice,
    loadNextFloorChoices,
    continueFromFloorEvent,
    retreat,
  } = useDungeon();
  const { goToHub } = useTwinKey();

  // フロアイベント結果
  const floorEventResult = state.floorEventResult;

  // 画面読み込み時にフロア選択肢を取得
  useEffect(() => {
    if (dungeon && floorChoices.length === 0) {
      loadNextFloorChoices();
    }
  }, [dungeon, floorChoices.length, loadNextFloorChoices]);

  // ダンジョン探索中かどうか
  const isExploring = !!dungeon;

  if (!dungeon || !isExploring) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={goToHub}>
            ← 戻る
          </button>
          <h1 style={styles.title}>ダンジョン</h1>
        </div>
        <div style={styles.emptyMessage}>
          ダンジョン情報がありません
        </div>
      </div>
    );
  }

  // 難易度の星の数
  const difficultyStars = DIFFICULTY_STARS[dungeon.difficulty] || 1;

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.dungeonHeader}>
        <div style={styles.dungeonInfo}>
          <h1 style={styles.dungeonName}>{dungeon.name}</h1>
          <div style={styles.dungeonSubInfo}>
            {dungeon.depthName} | 難易度: {'★'.repeat(difficultyStars)} ({dungeon.difficulty})
          </div>
        </div>
        <div style={styles.floorInfo}>
          <div style={styles.currentFloorLabel}>現在の階層</div>
          <div style={styles.currentFloorNumber}>{currentFloor}F</div>
          <div style={styles.totalFloors}>/ {maxFloor}F</div>
        </div>
      </div>

      {/* 進行バー */}
      <div style={styles.progressBarContainer}>
        <div
          style={{
            ...styles.progressBar,
            width: `${maxFloor > 0 ? (currentFloor / maxFloor) * 100 : 0}%`,
          }}
        />
        <div style={styles.progressText}>
          進行度: {maxFloor > 0 ? Math.floor((currentFloor / maxFloor) * 100) : 0}%
        </div>
      </div>

      {/* パーティステータス */}
      <div style={styles.partyStatusBar}>
        {(state.activeParty?.members || []).map((member, index) => (
          <MiniPartyMember key={member?.uniqueId || index} member={member} />
        ))}
      </div>

      {/* 階層選択 */}
      <div style={styles.floorChoicesSection}>
        <h2 style={styles.choiceTitle}>次の行き先を選択</h2>
        <div style={styles.floorChoicesGrid}>
          {floorChoices.map((choice, index) => (
            <FloorChoiceCard
              key={index}
              choice={choice}
              onClick={() => selectFloorChoice(choice)}
            />
          ))}
        </div>
      </div>

      {/* ダンジョン背景効果 */}
      <div style={styles.dungeonEffects}>
        {dungeon.variation?.gimmick && (
          <div style={styles.dungeonGimmick}>
            🎭 {dungeon.variation.gimmick}
          </div>
        )}
        {dungeon.variation?.timeOfDay && (
          <div style={styles.dungeonTime}>
            🕐 {dungeon.variation.timeOfDay}
          </div>
        )}
      </div>

      {/* 撤退ボタン */}
      <div style={styles.retreatSection}>
        <button style={styles.retreatButton} onClick={retreat}>
          🚪 撤退
        </button>
        <div style={styles.retreatWarning}>
          ※ 撤退しても1日経過します
        </div>
      </div>

      {/* フロアイベント結果モーダル */}
      {floorEventResult && (
        <FloorEventResultModal
          result={floorEventResult}
          onContinue={continueFromFloorEvent}
        />
      )}
    </div>
  );
}

/**
 * ミニパーティメンバー表示
 * 装備効果を反映したステータスを表示
 */
function MiniPartyMember({ member }) {
  // 装備効果を反映したステータスを取得
  const modifiedStats = useMemo(() => {
    return getEquipmentModifiedStats(member);
  }, [member]);

  const hpPercent = modifiedStats.maxHp
    ? (modifiedStats.currentHp / modifiedStats.maxHp) * 100
    : 100;
  const hpColor = styles.getHpColor(hpPercent);
  const attrColor = styles.getAttributeColor(member.attribute);

  const isAlive = modifiedStats.currentHp > 0;

  return (
    <div
      style={{
        ...styles.miniMember,
        opacity: isAlive ? 1 : 0.4,
      }}
    >
      <div
        style={{
          ...styles.miniMemberIcon,
          background: attrColor,
        }}
      >
        {styles.getAttributeIcon(member.attribute)}
      </div>
      <div style={styles.miniMemberInfo}>
        <div style={styles.miniMemberNameRow}>
          <span style={styles.miniMemberName}>{member.name}</span>
          <span style={styles.miniMemberLevel}>Lv.{member.level || 1}</span>
        </div>
        <div style={styles.miniHpBarContainer}>
          <div
            style={{
              ...styles.miniHpBar,
              width: `${hpPercent}%`,
              background: hpColor,
            }}
          />
        </div>
        <div style={styles.miniHpText}>
          {modifiedStats.currentHp || 0} / {modifiedStats.maxHp || 0}
        </div>
      </div>
    </div>
  );
}

/**
 * 階層選択カード
 */
function FloorChoiceCard({ choice, onClick }) {
  const typeStyles = getFloorTypeStyles(choice.type);

  return (
    <div
      style={{
        ...styles.floorChoiceCard,
        borderColor: typeStyles.color,
        background: typeStyles.background,
      }}
      onClick={onClick}
    >
      <div style={styles.floorChoiceIcon}>{typeStyles.icon}</div>
      <div style={styles.floorChoiceInfo}>
        <div style={styles.floorChoiceType}>{typeStyles.name}</div>
        <div style={styles.floorChoiceDescription}>
          {getFloorDescription(choice)}
        </div>
        {choice.enemies && (
          <div style={styles.floorEnemyPreview}>
            敵: {choice.enemies.length}体
            {choice.enemies[0]?.name && ` (${choice.enemies[0].name} 他)`}
          </div>
        )}
        {choice.reward && (
          <div style={styles.floorRewardPreview}>
            報酬: {choice.reward.gold}G
          </div>
        )}
      </div>
      <div style={styles.floorChoiceArrow}>→</div>
    </div>
  );
}

/**
 * 階層タイプのスタイルを取得
 */
function getFloorTypeStyles(type) {
  const typeMap = {
    battle: {
      icon: '⚔️',
      name: '通常戦闘',
      color: '#ff6b6b',
      background: 'rgba(255, 107, 107, 0.1)',
    },
    strong_enemy: {
      icon: '💀',
      name: '強敵',
      color: '#9d4ce6',
      background: 'rgba(157, 76, 230, 0.1)',
    },
    treasure: {
      icon: '💎',
      name: '宝箱',
      color: '#ffd700',
      background: 'rgba(255, 215, 0, 0.1)',
    },
    event: {
      icon: '❓',
      name: 'イベント',
      color: '#6b9eff',
      background: 'rgba(107, 158, 255, 0.1)',
    },
    trap: {
      icon: '⚠️',
      name: '罠',
      color: '#ff9500',
      background: 'rgba(255, 149, 0, 0.1)',
    },
    rest: {
      icon: '🏕️',
      name: '休息',
      color: '#6bff6b',
      background: 'rgba(107, 255, 107, 0.1)',
    },
    boss: {
      icon: '👑',
      name: 'ボス',
      color: '#ff4444',
      background: 'rgba(255, 68, 68, 0.15)',
    },
  };

  return typeMap[type] || typeMap.battle;
}

/**
 * 階層の説明を取得
 */
function getFloorDescription(choice) {
  switch (choice.type) {
    case 'battle':
      return 'モンスターとの戦闘が発生します';
    case 'strong_enemy':
      return '強力な敵が待ち構えています';
    case 'treasure':
      return '宝箱を発見できます';
    case 'event':
      return '何かが起こります...';
    case 'trap':
      return '罠があるかもしれません';
    case 'rest':
      return 'HPを回復できます';
    case 'boss':
      return 'ボスとの決戦です';
    default:
      return '';
  }
}

/**
 * フロアイベント結果モーダル
 */
function FloorEventResultModal({ result, onContinue }) {
  const { type, title, message, details } = result;

  // タイプ別の背景色
  const typeColors = {
    rest: 'rgba(107, 255, 107, 0.15)',
    treasure: 'rgba(255, 215, 0, 0.15)',
    event: 'rgba(107, 158, 255, 0.15)',
    trap: 'rgba(255, 149, 0, 0.15)',
  };

  return (
    <div style={styles.eventModalOverlay}>
      <div
        style={{
          ...styles.eventModalContent,
          background: typeColors[type] || '#1a1a2e',
        }}
      >
        <h2 style={styles.eventModalTitle}>{title}</h2>
        <p style={styles.eventModalMessage}>{message}</p>

        {/* 詳細情報 */}
        <div style={styles.eventModalDetails}>
          {/* 休息の場合：回復量表示 */}
          {type === 'rest' && Array.isArray(details) && details.length > 0 && (
            <div style={styles.healResultList}>
              {details.map((heal, index) => (
                <div key={index} style={styles.healResultItem}>
                  <span style={styles.healName}>{heal.name}</span>
                  <span style={styles.healAmount}>+{heal.healAmount} HP</span>
                </div>
              ))}
            </div>
          )}

          {/* 宝箱の場合：報酬表示 */}
          {type === 'treasure' && details && (
            <div style={styles.treasureRewards}>
              {details.gold > 0 && (
                <div style={styles.rewardItem}>
                  <span>💰 探索G</span>
                  <span style={styles.rewardValue}>+{details.gold} G</span>
                </div>
              )}
              {details.materials && details.materials.length > 0 && (
                <div style={treasureDropStyles.dropsSection}>
                  <div style={treasureDropStyles.dropsTitle}>⚗️ 獲得素材</div>
                  <div style={treasureDropStyles.dropsGrid}>
                    {(() => {
                      // 素材をIDでグループ化
                      const groupedMaterials = details.materials.reduce((acc, mat) => {
                        const materialId = mat.item || mat.type;
                        acc[materialId] = (acc[materialId] || 0) + mat.count;
                        return acc;
                      }, {});
                      return Object.entries(groupedMaterials).map(([materialId, count]) => {
                        const material = getMaterialByIdOrName(materialId);
                        return (
                          <div key={materialId} style={treasureDropStyles.dropItem}>
                            <span style={treasureDropStyles.dropIcon}>{material.icon}</span>
                            <span style={treasureDropStyles.dropName}>{material.name}</span>
                            <span style={treasureDropStyles.dropCount}>x{count}</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* イベントの場合：効果表示 */}
          {type === 'event' && details && (
            <div style={styles.eventEffects}>
              {details.effect && (
                <div style={styles.effectItem}>効果: {details.effect}</div>
              )}
              {details.gold && (
                <div style={styles.rewardItem}>
                  <span>💰 獲得</span>
                  <span style={styles.rewardValue}>+{details.gold} G</span>
                </div>
              )}
              {details.damages && details.damages.length > 0 && (
                <div style={styles.damageResultList}>
                  {details.damages.map((dmg, index) => (
                    <div key={index} style={styles.damageResultItem}>
                      <span style={styles.damageName}>{dmg.name}</span>
                      <span style={styles.damageAmount}>-{dmg.damage} HP</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 罠の場合：ダメージ表示 */}
          {type === 'trap' && details?.damages && (
            <div style={styles.damageResultList}>
              {details.damages.map((dmg, index) => (
                <div key={index} style={styles.damageResultItem}>
                  <span style={styles.damageName}>{dmg.name}</span>
                  <span style={styles.damageAmount}>-{dmg.damage} HP</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <button style={styles.eventModalButton} onClick={onContinue}>
          次のフロアへ →
        </button>
      </div>
    </div>
  );
}

// 宝箱ドロップ表示用スタイル（BattleScreenと同様）
const treasureDropStyles = {
  dropsSection: {
    marginTop: '12px',
    padding: '12px',
    background: 'rgba(156, 39, 176, 0.15)',
    borderRadius: '8px',
    border: '1px solid rgba(156, 39, 176, 0.3)',
  },
  dropsTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#ce93d8',
    marginBottom: '8px',
  },
  dropsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  dropItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '4px',
    fontSize: '12px',
  },
  dropIcon: {
    fontSize: '14px',
  },
  dropName: {
    color: '#e1bee7',
  },
  dropCount: {
    color: '#4CAF50',
    fontWeight: 'bold',
  },
};

export default DungeonScreen;
